// Data Service - Single source with caching
// API called ONLY ONCE, then cached in memory

import { apiGet, apiPost } from './apiService';
import { therapies as staticTherapies, servicesData as staticServices, faqData as staticFAQs, coursesData as staticCourses } from '../data/siteContent';

// Helper function to extract YouTube ID from URL
function extractYouTubeId(url) {
  if (!url) return '';
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : '';
}

// In-memory caches
let settingsCache = null;
let therapiesCache = null;
let servicesCache = null;
let coursesCache = null;
let faqsCache = null;
let blogsCache = null;
let videosCache = null;
let testimonialsCache = null;
let teamCache = null;
let awardsCache = null;
let galleryCache = null;
let termsCache = null;
let privacyCache = null;
let cookiePolicyCache = null;

// Global promises to share results
let settingsPromise = null;
let therapiesPromise = null;
let servicesPromise = null;
let coursesPromise = null;
let faqsPromise = null;
let blogsPromise = null;
let videosPromise = null;
let testimonialsPromise = null;
let teamPromise = null;
let awardsPromise = null;
let galleryPromise = null;
let termsPromise = null;
let privacyPromise = null;
let cookiePolicyPromise = null;

export async function getSettings() {
  if (settingsCache) {
    return settingsCache; // ✅ Return cached data
  }

  if (settingsPromise) {
    return await settingsPromise; // Wait for existing request
  }

  settingsPromise = (async () => {
    try {
      const data = await apiGet('settings', { limit: 1 });
      settingsCache = data.settings || {};
      return settingsCache;
    } catch (error) {
      console.error('❌ Failed to get settings:', error);
      settingsCache = {};
      return settingsCache;
    } finally {
      settingsPromise = null; // Reset promise after completion
    }
  })();

  return await settingsPromise;
}

export async function getTherapies() {
  if (therapiesCache) {
    return therapiesCache; // ✅ Return cached data
  }

  if (therapiesPromise) {
    return await therapiesPromise; // Wait for existing request
  }

  therapiesPromise = (async () => {
    try {
      // Add timeout to API call
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('API timeout')), 5000); // 5 second timeout
      });

      const apiPromise = apiGet('therapies');
      const data = await Promise.race([apiPromise, timeoutPromise]);

      if (data.success && data.therapies) {
        therapiesCache = data.therapies.map(therapy => ({
          id: therapy.id.toString(),
          title: therapy.title,
          tagline: therapy.tagline || 'Therapy description', // Short description for dropdowns
          description: therapy.description || '', // Full rich text description
          image: therapy.image_url || '/images/theropy/default.webp',
          path: therapy.path || `/therapy/${therapy.title.toLowerCase().replace(/\s+/g, '-')}`
        }));
        return therapiesCache;
      }
    } catch (error) {
      console.error('Failed to get therapies from API, using static fallback:', error);
    } finally {
      therapiesPromise = null; // Reset promise after completion
    }

    // Fallback to static data
    console.log('Using static therapies data as fallback');
    therapiesCache = staticTherapies;
    return therapiesCache;
  })();

  return await therapiesPromise;
}

export async function getServices() {
  if (servicesCache) {
    return servicesCache; // ✅ Return cached data
  }

  if (servicesPromise) {
    return await servicesPromise; // Wait for existing request
  }

  servicesPromise = (async () => {
    try {
      const data = await apiGet('services');
      if (data.services) {
        servicesCache = data.services.map(service => ({
          id: service.id,
          title: service.title,
          slug: service.slug,
          shortDescription: service.short_description,
          description: service.description,
          image: service.image_url || 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=600',
          features: service.features || []
        }));
        return servicesCache;
      }
    } catch (error) {
      console.error('Failed to get services:', error);
    } finally {
      servicesPromise = null; // Reset promise after completion
    }

    // Fallback to empty array if API fails
    servicesCache = [];
    return servicesCache;
  })();

  return await servicesPromise;
}

export async function getCourses() {
  if (coursesCache) {
    return coursesCache; // ✅ Return cached data
  }

  if (coursesPromise) {
    return await coursesPromise; // Wait for existing request
  }

  coursesPromise = (async () => {
    try {
      const data = await apiGet('courses');
      if (data.success && data.courses) {
        coursesCache = data.courses.map(course => ({
          title: course.title,
          duration: course.duration || 'Duration not specified',
          level: 'Professional Certification',
          desc: course.description || 'Course description',
          price: course.price ? `₹${course.price}` : 'Price on request',
          image: course.image_url || 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600'
        }));
        return coursesCache;
      }
    } catch (error) {
      console.error('Failed to get courses:', error);
    } finally {
      coursesPromise = null; // Reset promise after completion
    }

    // Fallback to static data
    coursesCache = staticCourses;
    return coursesCache;
  })();

  return await coursesPromise;
}

export async function getFAQs() {
  if (faqsCache) {
    return faqsCache; // ✅ Return cached data
  }

  if (faqsPromise) {
    return await faqsPromise; // Wait for existing request
  }

  faqsPromise = (async () => {
    try {
      const data = await apiGet('faq');
      if (data.success && data.faqs) {
        faqsCache = data.faqs.map(faq => ({
          question: faq.question,
          answer: faq.answer
        }));
        return faqsCache;
      }
    } catch (error) {
      console.error('Failed to get FAQs:', error);
    } finally {
      faqsPromise = null; // Reset promise after completion
    }

    // Fallback to static data
    faqsCache = staticFAQs;
    return faqsCache;
  })();

  return await faqsPromise;
}

export async function getBlogs() {
  if (blogsCache) {
    return blogsCache; // ✅ Return cached data
  }

  if (blogsPromise) {
    return await blogsPromise; // Wait for existing request
  }

  blogsPromise = (async () => {
    try {
      const data = await apiGet('blog');
      if (data.posts) {
        blogsCache = data.posts.map(blog => ({
          id: blog.id,
          title: blog.title,
          content: blog.content,
          excerpt: blog.excerpt,
          image: blog.featured_image,
          slug: blog.slug,
          created_at: blog.created_at,
          updated_at: blog.updated_at,
          author: blog.author_name,
          category: blog.category_name,
          tags: JSON.parse(blog.tags || '[]'),
          status: blog.is_published ? 'published' : 'draft',
          featured: blog.is_featured === 1,
          publishedAt: blog.published_at
        }));
        return blogsCache;
      }
    } catch (error) {
      console.error('❌ Failed to get blogs:', error);
    } finally {
      blogsPromise = null; // Reset promise after completion
    }

    // Fallback to empty array if API fails
    blogsCache = [];
    return blogsCache;
  })();

  return await blogsPromise;
}

export async function getVideos() {
  if (videosCache) {
    return videosCache; // ✅ Return cached data
  }

  if (videosPromise) {
    return await videosPromise; // Wait for existing request
  }

  videosPromise = (async () => {
    try {
      const data = await apiGet('videos');
      if (data.success && data.videos) {
        videosCache = data.videos.map(video => ({
          id: video.id,
          title: video.title,
          description: video.description,
          youtubeId: video.youtube_link ? extractYouTubeId(video.youtube_link) : '',
          youtubeLink: video.youtube_link,
          category: video.category || 'General',
          patient: video.patient_name,
          featured: video.is_featured === 1,
          createdAt: video.created_at
        }));
        return videosCache;
      }
    } catch (error) {
      console.error('❌ Failed to get videos:', error);
    } finally {
      videosPromise = null; // Reset promise after completion
    }

    // Fallback to empty array if API fails
    videosCache = [];
    return videosCache;
  })();

  return await videosPromise;
}

export async function getTestimonials() {
  if (testimonialsCache) {
    return testimonialsCache; // ✅ Return cached data
  }

  if (testimonialsPromise) {
    return await testimonialsPromise; // Wait for existing request
  }

  testimonialsPromise = (async () => {
    try {
      const data = await apiGet('testimonials');
      if (data.testimonials) {
        testimonialsCache = data.testimonials.map(testimonial => ({
          id: testimonial.id,
          title: testimonial.title,
          description: testimonial.description,
          youtubeId: testimonial.youtube_link ? extractYouTubeId(testimonial.youtube_link) : '',
          youtubeLink: testimonial.youtube_link,
          name: testimonial.name,
          featured: testimonial.is_featured === 1,
          createdAt: testimonial.created_at
        }));
        return testimonialsCache;
      }
    } catch (error) {
      console.error('❌ Failed to get testimonials:', error);
    } finally {
      testimonialsPromise = null; // Reset promise after completion
    }

    // Fallback to empty array if API fails
    testimonialsCache = [];
    return testimonialsCache;
  })();

  return await testimonialsPromise;
}

export async function getTeam() {
  if (teamCache) {
    return teamCache; // ✅ Return cached data
  }

  if (teamPromise) {
    return await teamPromise; // Wait for existing request
  }

  teamPromise = (async () => {
    try {
      const data = await apiGet('team');
      if (data.success && data.team) {
        teamCache = data.team.map(member => ({
          id: member.id,
          name: member.name,
          role: member.position,
          bio: member.bio,
          image: member.image_url || '/images/doctors/default.jpg',
          email: member.email,
          phone: member.phone,
          linkedin: member.linkedin_url,
          twitter: member.twitter_url,
          skills: member.skills ? member.skills.split(', ') : [],
          languages: member.languages ? member.languages.split(', ') : [],
          qualifications: member.qualifications,
          sortOrder: member.sort_order,
          isActive: member.is_active === 1,
          createdAt: member.created_at,
          updatedAt: member.updated_at
        }));
        return teamCache;
      }
    } catch (error) {
      console.error('❌ Failed to get team:', error);
    } finally {
      teamPromise = null; // Reset promise after completion
    }

    // Fallback to empty array if API fails
    teamCache = [];
    return teamCache;
  })();

  return await teamPromise;
}

export async function getAwards(category = null) {
  // Create cache key based on category
  const cacheKey = category ? `awards_${category}` : 'awards_all';
  
  if (!awardsCache) awardsCache = {};
  if (awardsCache[cacheKey]) {
    return awardsCache[cacheKey]; // ✅ Return cached data
  }

  if (!awardsPromise) awardsPromise = {};
  if (awardsPromise[cacheKey]) {
    return await awardsPromise[cacheKey]; // Wait for existing request
  }

  awardsPromise[cacheKey] = (async () => {
    try {
      const params = category ? { category } : {};
      const data = await apiGet('awards', params);
      if (data.success && data.awards) {
        const awards = data.awards.map(award => ({
          id: award.id,
          title: award.title,
          image: award.image_url,
          category: award.category,
          sortOrder: award.sort_order,
          isActive: award.is_active === 1,
          createdAt: award.created_at,
          updatedAt: award.updated_at
        }));
        awardsCache[cacheKey] = awards;
        return awards;
      }
    } catch (error) {
      console.error('❌ Failed to get awards:', error);
    } finally {
      awardsPromise[cacheKey] = null; // Reset promise after completion
    }

    // Fallback to empty array if API fails
    awardsCache[cacheKey] = [];
    return awardsCache[cacheKey];
  })();

  return await awardsPromise[cacheKey];
}

export async function getGallery(category = null, limit = 50, offset = 0) {
  const cacheKey = category ? `gallery-${category}-${limit}-${offset}` : `gallery-all-${limit}-${offset}`;

  if (galleryCache && galleryCache[cacheKey]) {
    return galleryCache[cacheKey]; // ✅ Return cached data
  }

  if (!galleryPromise) {
    galleryPromise = {};
  }

  if (galleryPromise[cacheKey]) {
    return await galleryPromise[cacheKey]; // Wait for existing request
  }

  galleryPromise[cacheKey] = (async () => {
    try {
      const params = { limit, offset };
      if (category) params.category = category;

      const data = await apiGet('gallery', params);

      if (data && data.gallery) {
        const gallery = data.gallery.map(item => ({
          id: item.id,
          title: item.title,
          description: item.description,
          url: item.image_url,
          category: item.category,
          isFeatured: item.is_featured === 1,
          isActive: item.is_active === 1,
          createdAt: item.created_at,
          updatedAt: item.updated_at
        }));

        if (!galleryCache) galleryCache = {};
        galleryCache[cacheKey] = gallery;
        return gallery;
      }
    } catch (error) {
      console.error('❌ Failed to get gallery:', error);
    } finally {
      galleryPromise[cacheKey] = null; // Reset promise after completion
    }

    // Fallback to empty array if API fails
    if (!galleryCache) galleryCache = {};
    galleryCache[cacheKey] = [];
    return galleryCache[cacheKey];
  })();

  return await galleryPromise[cacheKey];
}

export async function submitContactForm(formData) {
  try {
    const response = await apiPost('contact', {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      message: formData.message,
      project_type: formData.primaryConcern || 'general',
      source: 'website_contact_form'
    });

    if (response.success) {
      return {
        success: true,
        message: response.message || 'Thank you for your message! We\'ll get back to you within 24 hours.'
      };
    } else {
      throw new Error(response.message || 'Failed to submit form');
    }
  } catch (error) {
    console.error('❌ Failed to submit contact form:', error);
    throw error;
  }
}

export async function getTerms() {
  if (termsCache) {
    return termsCache; // ✅ Return cached data
  }

  if (termsPromise) {
    return await termsPromise; // Wait for existing request
  }

  termsPromise = (async () => {
    try {
      const data = await apiGet('terms');
      if (data.success && data.terms) {
        termsCache = data.terms;
        return termsCache;
      }
    } catch (error) {
      console.error('Failed to get terms:', error);
    } finally {
      termsPromise = null; // Reset promise after completion
    }

    // Fallback to empty object if API fails
    termsCache = { title: 'Terms of Service', content: 'Terms content not available at the moment.' };
    return termsCache;
  })();

  return await termsPromise;
}

export async function getPrivacy() {
  if (privacyCache) {
    return privacyCache; // ✅ Return cached data
  }

  if (privacyPromise) {
    return await privacyPromise; // Wait for existing request
  }

  privacyPromise = (async () => {
    try {
      const data = await apiGet('privacy');
      if (data.success && data.privacy) {
        privacyCache = data.privacy;
        return privacyCache;
      }
    } catch (error) {
      console.error('Failed to get privacy:', error);
    } finally {
      privacyPromise = null; // Reset promise after completion
    }

    // Fallback to empty object if API fails
    privacyCache = { title: 'Privacy Policy', content: 'Privacy policy content not available at the moment.' };
    return privacyCache;
  })();

  return await privacyPromise;
}

export async function getCookiePolicy() {
  if (cookiePolicyCache) {
    return cookiePolicyCache; // ✅ Return cached data
  }

  if (cookiePolicyPromise) {
    return await cookiePolicyPromise; // Wait for existing request
  }

  cookiePolicyPromise = (async () => {
    try {
      const data = await apiGet('cookie-policy');
      if (data.success && data.cookiePolicy) {
        cookiePolicyCache = data.cookiePolicy;
        return cookiePolicyCache;
      }
    } catch (error) {
      console.error('Failed to get cookie policy:', error);
    } finally {
      cookiePolicyPromise = null; // Reset promise after completion
    }

    // Fallback to empty object if API fails
    cookiePolicyCache = { title: 'Cookie Policy', content: 'Cookie policy content not available at the moment.' };
    return cookiePolicyCache;
  })();

  return await cookiePolicyPromise;
}

// SEO cache and promise
let seoCache = {};
let seoPromises = {};

export async function getSEO(pageType, pageSlug = null) {
  const cacheKey = `${pageType}_${pageSlug || 'null'}`;

  if (seoCache[cacheKey]) {
    return seoCache[cacheKey]; // ✅ Return cached data
  }

  if (seoPromises[cacheKey]) {
    return await seoPromises[cacheKey]; // Wait for existing request
  }

  seoPromises[cacheKey] = (async () => {
    try {
      const params = { page_type: pageType };
      if (pageSlug) params.page_slug = pageSlug;

      const data = await apiGet('seo', params);
      if (data && data.seo) {
        seoCache[cacheKey] = data.seo;
        return data.seo;
      }
    } catch (error) {
      console.error('❌ Failed to get SEO data:', error);
    } finally {
      seoPromises[cacheKey] = null; // Reset promise after completion
    }

    // Fallback to null if API fails
    seoCache[cacheKey] = null;
    return seoCache[cacheKey];
  })();

  return await seoPromises[cacheKey];
}

// Legacy class-based interface for backward compatibility
class DataService {
  async getSettings() {
    return getSettings();
  }

  async getTherapies() {
    return getTherapies();
  }

  async getServices() {
    return getServices();
  }

  async getCourses() {
    return getCourses();
  }

  async getFAQs() {
    return getFAQs();
  }

  async getBlogs() {
    return getBlogs();
  }

  async getVideos() {
    return getVideos();
  }

  async getTestimonials() {
    return getTestimonials();
  }

  async getTeam() {
    return getTeam();
  }

  async getAwards(category = null) {
    return getAwards(category);
  }

  async getGallery(category = null, limit = 50, offset = 0) {
    return getGallery(category, limit, offset);
  }

  async submitContactForm(formData) {
    return submitContactForm(formData);
  }

  async getTerms() {
    return getTerms();
  }

  async getPrivacy() {
    return getPrivacy();
  }

  async getCookiePolicy() {
    return getCookiePolicy();
  }

  async getSEO(pageType, pageSlug = null) {
    return getSEO(pageType, pageSlug);
  }
}

// Export both the functions and the class for compatibility
const dataService = new DataService();
export default dataService;